import {
    getPositionX as gpX,
    getPositionY as gpY,
    handleCorrPosition as hCorrPosition
} from 'components/pie-chart/helpers';

const MAX_SCORE = 500;
const START_ANGLE = -90;

export const safeSlicing = (score) => {
    if (score <= 15) {
        return 15;
    } else if (score === MAX_SCORE) {
        return MAX_SCORE - 0.001;
    }
    return score;
};

export const getPositionX = gpX;
export const getPositionY = gpY;
export const handleCorrPosition = hCorrPosition;

export const types = {
    s1: 'S1',
    s2: 'S2',
    s3: 'S3',
    s4: 'S4',
    cor: 'Cor'
};

export const calculatePanelHeight = (id, height, options = {}) => {
    const fieldHeight = 14;

    if (id !== types.cor) {
        return height;
    }
    let count = 0;
    if (options.corrS1S2) count++;
    if (options.corrS1S3) count++;
    if (options.corrS1S4) count++;
    if (options.corrS2S3) count++;
    if (options.corrS2S4) count++;
    if (options.corrS3S4) count++;

    return height + count * fieldHeight;
};

export const sliceOpt = {
    initial: (s) => ({
        opacity: 0,
        originX: '100px 100px',
        rotate: -1 * (s.absoluteStart || 30)
    }),
    start: (i) => ({
        opacity: 1,
        rotate: 0,
        transition: { duration: 1, delay: 1 + 1 * i, type: 'spring' }
    }),
    stop: {
        rotate: 0,
        opacity: 1
    }
};

export const panelOpt = {
    initial: { opacity: 0 },
    start: (i) => ({
        opacity: 1,
        transition: { duration: 1, delay: 1 + 1 * i + 0.7, type: 'spring' }
    }),
    stop: {
        opacity: 1
    }
};

export const rmScoreDecompositionAdaptor = (res) => [
    {
        id: types.s1,
        name: 'scenario1',
        label: 'S1',
        order: 1,
        score: res.s1Score,
        fill: '#678190',
        options: {
            title: 'Scenario 1',
            description: 'Negative client ROI w high RM revenue',
            rank: `${res.s1Rank}`,
            totalRevenue: res.totalRevenue,
            negativeDelta: res.negativeDelta
        }
    },
    {
        id: types.s2,
        name: 'scenario2',
        label: 'S2',
        order: 2,
        score: res.s2Score,
        fill: '#549AB5',
        options: {
            title: 'Scenario 2',
            description: 'Persistent dynamic suitability breaches',
            rank: `${res.s2Rank}`,
            crAlertRm: res.crAlertRm,
            crAlertPeer: res.crAlertPeer,
            prmAlertRm: res.prmAlertRm,
            prmAlertPeer: res.prmAlertPeer
        }
    },
    {
        id: types.s3,
        name: 'scenario3',
        label: 'S3',
        order: 3,
        score: res.s3Score,
        fill: '#A9B8C1',
        options: {
            title: 'Scenario 3',
            description: 'High number of execution-only trades',
            rank: `${res.s3Rank}`,
            eoTradesRm: res.eoTradesRm,
            eoTradesPeer: res.eoTradesPeer
        }
    },
    {
        id: types.s4,
        name: 'scenario4',
        label: 'S4',
        order: 4,
        score: res.s4Score,
        fill: '#7FADBF',
        options: {
            title: 'Scenario 4',
            description: 'High instances of concentrated execution only trades',
            rank: `${res.s4Rank}`,
            maxClient: res.maxClient
        }
    },
    {
        id: types.cor,
        name: 'correlation',
        label: 'Corr',
        order: 5,
        score: res.correlationScore,
        fill: '#9BCEE2',
        options: {
            title: 'Correlation',
            corrS1S2: res.corrS1S2,
            corrS1S3: res.corrS1S3,
            corrS1S4: res.corrS1S4,
            corrS2S3: res.corrS2S3,
            corrS2S4: res.corrS2S4,
            corrS3S4: res.corrS3S4
        }
    }
];

export const calculateValues = (sd) => {
    const angle = (safeSlicing(sd.score) * 360) / MAX_SCORE;

    const xRadius = 400;
    const yRadius = 175;
    const radius = 95;
    const maxRadius = 102;
    const angleStart = sd.start;
    const angleEnd = angleStart + angle;
    const diffAngle = (angleEnd - angleStart) / 2;
    const centerAngle = diffAngle + sd.absoluteStart;

    const x1 =
        xRadius + radius * Math.cos((Math.PI * (angleStart + diffAngle)) / 180);
    const y1 =
        yRadius + radius * Math.sin((Math.PI * (angleStart + diffAngle)) / 180);

    const x2 =
        xRadius +
        maxRadius * Math.cos((Math.PI * (angleStart + diffAngle)) / 180);
    const y2 =
        yRadius +
        maxRadius * Math.sin((Math.PI * (angleStart + diffAngle)) / 180);

    return { x1, y1, x2, y2, centerAngle };
};

export const calculateAngles = (array) => {
    const formattedArray = array.reduce((p, c, i) => {
        const prev = i === 0 ? {} : p[i - 1];
        const angle =
            ((safeSlicing(prev.score) || safeSlicing(c.score)) * 360) /
            MAX_SCORE;

        const start = i === 0 ? START_ANGLE : prev.start + angle;
        const absoluteStart =
            i === 0 ? START_ANGLE + 90 : prev.start + angle + 90;

        const { centerAngle, y2 } = calculateValues(
            {
                ...c,
                start,
                absoluteStart
            },
            { ...p[i - 1] }
        );

        return [
            ...p,
            {
                ...c,
                start,
                absoluteStart,
                centerAngle,
                y2
            }
        ];
    }, []);

    return formattedArray;
};
